import { useEffect, useState } from 'react';
import { fetchAuthorizedBranches } from '../utils/authorizedBranches';

// Session-cached branch list for display/picker surfaces (spec P2: cache
// /api/users/me/branches per session). Keyed by the session token so a
// sign-out/sign-in never reuses the previous user's branches. The server
// stays the authority on every request (x-garage-id is validated there);
// this cache only avoids re-fetching the same list on every form mount.
let cachedBranches = null;
let cachedToken = null;
let inFlight = null;

function sessionToken() {
  try {
    return localStorage.getItem('token') || '';
  } catch (e) {
    return '';
  }
}

export function loadMyBranches() {
  const token = sessionToken();
  if (cachedBranches && cachedToken === token) return Promise.resolve(cachedBranches.slice());
  if (cachedToken !== token) {
    // Token changed: the cached list belongs to the previous session. Drop it
    // and any fetch started for that session, so its late result can never be
    // committed under the new token.
    cachedBranches = null;
    inFlight = null;
    cachedToken = token;
  }
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(() => fetchAuthorizedBranches())
      .then((list) => {
        // Commit only if this fetch is still the current session's.
        if (cachedToken === token) {
          cachedBranches = Array.isArray(list) ? list : [];
          inFlight = null;
        }
        return Array.isArray(list) ? list : [];
      })
      .catch((err) => {
        if (cachedToken === token) inFlight = null;
        throw err;
      });
  }
  return inFlight.then((list) => list.slice());
}

// Tests only.
export function __resetMyBranchesCache() {
  cachedBranches = null;
  cachedToken = null;
  inFlight = null;
}

export function useMyBranches({ enabled = true } = {}) {
  const [branches, setBranches] = useState(() =>
    cachedBranches && cachedToken === sessionToken() ? cachedBranches.slice() : []
  );
  const [loading, setLoading] = useState(
    enabled && !(cachedBranches && cachedToken === sessionToken())
  );

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    loadMyBranches()
      .then((list) => {
        if (live) {
          setBranches(list);
          setLoading(false);
        }
      })
      .catch(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [enabled]);

  return { branches, loading, multiBranch: branches.length > 1 };
}

export default useMyBranches;
